import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChalkboardTeacher, Scan, ListChecks } from "@phosphor-icons/react";
import QRScanner from "@/components/QRScanner";
import RowActions from "@/components/RowActions";

const STATUS = ["hadir", "alfa", "izin", "sakit"];
const STATUS_COLOR = { hadir: "bg-green-100 text-green-800", alfa: "bg-red-100 text-red-700", izin: "bg-amber-100 text-amber-800", sakit: "bg-blue-100 text-blue-700" };

export default function AbsensiGuru() {
  const [tab, setTab] = useState("massal");
  const [ustazs, setUstazs] = useState([]);
  const [tanggal, setTanggal] = useState(() => new Date().toISOString().slice(0, 10));
  const [kegiatan, setKegiatan] = useState("");
  const [waktu, setWaktu] = useState("");
  const [kegiatanList, setKegiatanList] = useState([]);
  const [items, setItems] = useState([]);
  const [rowStatus, setRowStatus] = useState({});   // { ustaz_id: status }
  const [rowAlasan, setRowAlasan] = useState({});   // { ustaz_id: alasan }
  const [scanOpen, setScanOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState(() => new Date().toISOString().slice(0, 10));
  const [rekap, setRekap] = useState(null);
  const [rekapPeriod, setRekapPeriod] = useState("harian");
  const [rekapKegiatan, setRekapKegiatan] = useState("");
  const [rekapKegList, setRekapKegList] = useState([]);
  const [editing, setEditing] = useState(null);  // absensi row being edited

  const loadUstaz = async () => {
    try {
      const { data } = await api.get("/ustaz", { params: { status: "aktif" } });
      setUstazs(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const loadItems = async () => {
    try {
      const { data } = await api.get("/absensi/ustaz", { params: { tanggal: dateFilter } });
      setItems(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const loadRekap = async () => {
    try {
      const params = { period: rekapPeriod };
      if (rekapKegiatan) params.kegiatan = rekapKegiatan;
      const { data } = await api.get("/absensi/ustaz/rekap", { params });
      setRekap(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const loadRekapKegList = async () => {
    try {
      const { data } = await api.get("/absensi/ustaz/kegiatan-list");
      setRekapKegList(data);
    } catch (_) {}
  };
  const loadKegiatanForHari = async (tgl) => {
    // Load kegiatan list from master_jadwal based on the day-of-week of the date
    try {
      const d = new Date(tgl + "T00:00:00");
      const HARI = ["Ahad", "Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];
      const hari = HARI[d.getDay()];
      const { data } = await api.get("/master/jadwal_kegiatan", { params: { hari } });
      setKegiatanList(data);
    } catch (_) { setKegiatanList([]); }
  };
  useEffect(() => { loadUstaz(); }, []);
  useEffect(() => { loadItems(); /* eslint-disable-next-line */ }, [dateFilter]);
  useEffect(() => { if (tab === "rekap") { loadRekap(); loadRekapKegList(); } /* eslint-disable-next-line */ }, [tab, rekapPeriod, rekapKegiatan]);
  useEffect(() => { loadKegiatanForHari(tanggal); }, [tanggal]);

  const onKegiatanSelect = (nama) => {
    setKegiatan(nama);
    const found = kegiatanList.find((k) => k.nama === nama);
    if (found?.jam_mulai) setWaktu(found.jam_mulai);
  };

  const submitRow = async (ustazId) => {
    const status = rowStatus[ustazId] || "hadir";
    const alasan = rowAlasan[ustazId] || "";
    if (status === "izin" && !alasan.trim()) return toast.error("Alasan izin wajib diisi");
    if (!kegiatan) return toast.error("Pilih Nama Kegiatan terlebih dahulu");
    try {
      await api.post("/absensi/ustaz", { ustaz_id: ustazId, tanggal, kegiatan, waktu: waktu || undefined, status, alasan: alasan || undefined });
      const nama = ustazs.find((u) => u.id === ustazId)?.nama || "";
      toast.success(`${nama} → ${status}`);
      loadItems();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const submitAll = async () => {
    if (!kegiatan) return toast.error("Pilih Nama Kegiatan terlebih dahulu");
    const targets = ustazs.filter((u) => rowStatus[u.id]);
    if (targets.length === 0) return toast.error("Belum ada status yang dipilih");
    let ok = 0, fail = 0;
    for (const u of targets) {
      const status = rowStatus[u.id];
      const alasan = rowAlasan[u.id] || "";
      if (status === "izin" && !alasan.trim()) { fail++; continue; }
      try {
        await api.post("/absensi/ustaz", { ustaz_id: u.id, tanggal, kegiatan, waktu: waktu || undefined, status, alasan: alasan || undefined });
        ok++;
      } catch (_) { fail++; }
    }
    toast.success(`Berhasil: ${ok}${fail ? ` · Gagal: ${fail}` : ""}`);
    setRowStatus({}); setRowAlasan({}); loadItems();
  };

  const onScanned = async (text) => {
    setScanOpen(false);
    if (!kegiatan) return toast.error("Pilih Nama Kegiatan sebelum scan");
    try {
      await api.post("/absensi/ustaz", { kode: text, tanggal, kegiatan, waktu: waktu || undefined, status: "hadir" });
      toast.success(`Ustaz dengan kode ${text} tercatat hadir untuk ${kegiatan}`);
      loadItems();
    } catch (e) { toast.error(e.response?.data?.detail || `Kode tidak dikenali: ${text}`); }
  };

  const doEdit = async () => {
    try {
      await api.put(`/absensi/ustaz/${editing.id}`, {
        status: editing.status, alasan: editing.alasan || undefined, catatan: editing.catatan || undefined,
      });
      toast.success("Absensi diperbarui");
      setEditing(null); loadItems();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const doDelete = async (row) => {
    await api.delete(`/absensi/ustaz/${row.id}`);
    toast.success("Dihapus"); loadItems();
  };

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Sumber Daya</div>
        <h1 className="font-display text-3xl font-bold text-[#064E3B]">Absensi Ustaz / Ustazah</h1>
        <p className="text-sm text-slate-500 mt-1">Terpisah dari absensi santri. Metode <b>Manual</b> (daftar otomatis) atau <b>QR/Barcode</b>.</p>
      </div>

      <div className="imsyaf-card p-2 flex gap-1 flex-wrap">
        <button data-testid="tab-guru-massal" onClick={() => setTab("massal")}
          className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "massal" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          <ListChecks size={14} className="inline -mt-0.5 mr-1"/> Absensi Manual
        </button>
        <button data-testid="tab-guru-qr" onClick={() => setTab("qr")}
          className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "qr" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          <Scan size={14} className="inline -mt-0.5 mr-1"/> QR / Barcode
        </button>
        <button data-testid="tab-guru-rekap" onClick={() => setTab("rekap")}
          className={`px-4 py-2 rounded-md text-sm font-medium ${tab === "rekap" ? "bg-[#14532d] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          Rekap Kehadiran
        </button>
      </div>

      {tab === "massal" && (
        <>
          <div className="imsyaf-card p-4 flex items-end gap-3 flex-wrap">
            <div><label className="text-xs">Tanggal Absensi</label>
              <input type="date" className="imsyaf-input" value={tanggal} onChange={(e) => setTanggal(e.target.value)} data-testid="guru-tanggal" /></div>
            <button onClick={submitAll} className="imsyaf-btn-primary" data-testid="guru-save-all">Simpan Semua</button>
            <div className="text-xs text-slate-500 ml-auto">Total Ustaz/Ustazah aktif: <b>{ustazs.length}</b></div>
          </div>

          <div className="imsyaf-card overflow-hidden">
            <table className="w-full text-sm zebra">
              <thead className="bg-slate-50"><tr>
                <th className="text-left px-4 py-2 font-semibold">Nama</th>
                <th className="text-left px-4 py-2 font-semibold">Jabatan</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Alasan (Izin)</th>
                <th className="text-right px-4 py-2 font-semibold">Aksi</th>
              </tr></thead>
              <tbody>
                {ustazs.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`guru-row-${u.id}`}>
                    <td className="px-4 py-2 font-medium flex items-center gap-2">
                      {u.foto_url ? <img src={u.foto_url} alt="" className="w-8 h-8 rounded-full object-cover"/> : <div className="w-8 h-8 rounded-full bg-green-100"/>}
                      {u.nama}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{u.jabatan || "-"}</td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1 flex-wrap">
                        {STATUS.map((s) => (
                          <button key={s} onClick={() => setRowStatus({ ...rowStatus, [u.id]: s })}
                            data-testid={`guru-${u.id}-${s}`}
                            className={`px-2 py-1 text-[11px] rounded ${rowStatus[u.id] === s ? STATUS_COLOR[s] + " ring-2 ring-[#14532d]" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                            {s.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      {rowStatus[u.id] === "izin" && (
                        <input placeholder="Alasan izin *" className="imsyaf-input text-xs"
                          value={rowAlasan[u.id] || ""}
                          onChange={(e) => setRowAlasan({ ...rowAlasan, [u.id]: e.target.value })}
                          data-testid={`guru-${u.id}-alasan`} />
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => submitRow(u.id)} disabled={!rowStatus[u.id]}
                        className="imsyaf-btn-outline text-xs disabled:opacity-40"
                        data-testid={`guru-save-${u.id}`}>Simpan</button>
                    </td>
                  </tr>
                ))}
                {ustazs.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">Belum ada Ustaz/Ustazah. Tambahkan di halaman Data Ustaz.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "qr" && (
        <div className="space-y-3">
          <div className="imsyaf-card p-5 grid md:grid-cols-3 gap-3">
            <div><label className="text-xs">Tanggal</label>
              <input type="date" className="imsyaf-input" value={tanggal} onChange={(e) => setTanggal(e.target.value)} /></div>
            <div><label className="text-xs">Nama Kegiatan *</label>
              <select className="imsyaf-input" value={kegiatan} onChange={(e) => onKegiatanSelect(e.target.value)} data-testid="guru-qr-kegiatan">
                <option value="">— Pilih Kegiatan —</option>
                {kegiatanList.map((k) => <option key={k.id} value={k.nama}>{k.nama}{k.jam_mulai ? ` (${k.jam_mulai})` : ""}</option>)}
              </select></div>
            <div><label className="text-xs">Waktu Kegiatan</label>
              <input type="time" className="imsyaf-input" value={waktu} onChange={(e) => setWaktu(e.target.value)} /></div>
            <div className="md:col-span-3 flex items-end">
              <button onClick={() => setScanOpen(true)} disabled={!kegiatan} className="imsyaf-btn-primary w-full disabled:opacity-50" data-testid="guru-scan-btn">
                <Scan size={18}/> Buka Scanner QR/Barcode
              </button>
            </div>
          </div>
          {scanOpen && (
            <div className="imsyaf-card p-5">
              <QRScanner onScan={onScanned} onError={(m) => { setScanOpen(false); toast.error(m); }} />
              <div className="text-right mt-3"><button onClick={() => setScanOpen(false)} className="imsyaf-btn-outline">Tutup</button></div>
            </div>
          )}
        </div>
      )}

      {tab === "rekap" && rekap && (
        <div className="space-y-3">
          <div className="imsyaf-card p-4 flex gap-3 flex-wrap items-end">
            <div className="flex gap-1 flex-wrap">
              {["harian", "mingguan", "bulanan", "tahunan"].map((p) => (
                <button key={p} onClick={() => setRekapPeriod(p)} data-testid={`rekap-${p}`}
                  className={`px-4 py-2 rounded text-sm font-medium ${rekapPeriod === p ? "bg-[#14532d] text-white" : "bg-slate-100 text-slate-600"}`}>
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="min-w-[220px]"><label className="text-xs">Filter Kegiatan</label>
              <select className="imsyaf-input" value={rekapKegiatan} onChange={(e) => setRekapKegiatan(e.target.value)} data-testid="rekap-kegiatan">
                <option value="">— Semua Kegiatan —</option>
                {rekapKegList.map((k) => <option key={k} value={k}>{k}</option>)}
              </select></div>
            <div className="text-xs text-slate-500 ml-auto self-center">
              Periode: <b>{rekap.from}</b> – <b>{rekap.to}</b>{rekap.kegiatan ? <> · Kegiatan: <b>{rekap.kegiatan}</b></> : null}
            </div>
          </div>
          <div className="imsyaf-card overflow-hidden">
            <table className="w-full text-sm zebra">
              <thead className="bg-slate-50"><tr>
                <th className="text-left px-4 py-2">Nama</th><th className="text-left px-4 py-2">Jabatan</th>
                <th className="text-center px-4 py-2 text-green-800">Hadir</th>
                <th className="text-center px-4 py-2 text-red-700">Alfa</th>
                <th className="text-center px-4 py-2 text-amber-800">Izin</th>
                <th className="text-center px-4 py-2 text-blue-700">Sakit</th>
                <th className="text-center px-4 py-2">Total</th>
              </tr></thead>
              <tbody>{rekap.items.map((r) => (
                <tr key={r.ustaz_id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium">{r.ustaz_nama || "-"}</td>
                  <td className="px-4 py-2 text-xs">{r.ustaz_jabatan || "-"}</td>
                  <td className="px-4 py-2 text-center font-bold text-green-700">{r.hadir}</td>
                  <td className="px-4 py-2 text-center font-bold text-red-700">{r.alfa}</td>
                  <td className="px-4 py-2 text-center font-bold text-amber-700">{r.izin}</td>
                  <td className="px-4 py-2 text-center font-bold text-blue-700">{r.sakit}</td>
                  <td className="px-4 py-2 text-center">{r.total}</td>
                </tr>
              ))}
              {rekap.items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Belum ada data pada periode ini.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab !== "rekap" && (
        <>
          <div className="imsyaf-card p-4 flex items-end gap-3">
            <div><label className="text-xs">Filter Tanggal</label>
              <input type="date" className="imsyaf-input" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} /></div>
            <div className="text-sm text-slate-500 pb-2">Total: <b>{items.length}</b></div>
          </div>
          <div className="imsyaf-card overflow-hidden">
            <table className="w-full text-sm zebra">
              <thead className="bg-slate-50"><tr>
                <th className="text-left px-4 py-2 font-semibold">Waktu</th>
                <th className="text-left px-4 py-2 font-semibold">Nama</th>
                <th className="text-left px-4 py-2 font-semibold">Jabatan</th>
                <th className="text-left px-4 py-2 font-semibold">Kegiatan</th>
                <th className="text-left px-4 py-2 font-semibold">Waktu Keg.</th>
                <th className="text-left px-4 py-2 font-semibold">Status</th>
                <th className="text-left px-4 py-2 font-semibold">Alasan</th>
                <th className="text-right px-4 py-2 font-semibold">Aksi</th>
              </tr></thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`guru-hist-${a.id}`}>
                    <td className="px-4 py-2 text-xs">{a.created_at?.slice(11, 16)}</td>
                    <td className="px-4 py-2 font-medium">{a.ustaz_nama}</td>
                    <td className="px-4 py-2 text-xs">{a.ustaz_jabatan || "-"}</td>
                    <td className="px-4 py-2 text-xs">{a.kegiatan || "-"}</td>
                    <td className="px-4 py-2 text-xs">{a.waktu || "-"}</td>
                    <td className="px-4 py-2"><span className={`imsyaf-badge ${STATUS_COLOR[a.status] || "bg-slate-100"}`}>{a.status}</span></td>
                    <td className="px-4 py-2 text-xs text-slate-500">{a.alasan || a.catatan || "-"}</td>
                    <td className="px-4 py-2 text-right">
                      <RowActions testid={`guru-hist-${a.id}`} onEdit={() => setEditing({ ...a })} onDelete={() => doDelete(a)} confirmText="Hapus absensi ini?" />
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Belum ada absensi hari ini.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="edit-guru-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full space-y-3">
            <h3 className="font-display text-lg font-bold text-[#064E3B]">Edit Absensi — {editing.ustaz_nama}</h3>
            <div><label className="text-xs">Status</label>
              <select className="imsyaf-input" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} data-testid="edit-guru-status">
                {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select></div>
            <div><label className="text-xs">Alasan</label>
              <input className="imsyaf-input" value={editing.alasan || ""} onChange={(e) => setEditing({ ...editing, alasan: e.target.value })} /></div>
            <div><label className="text-xs">Catatan</label>
              <input className="imsyaf-input" value={editing.catatan || ""} onChange={(e) => setEditing({ ...editing, catatan: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="imsyaf-btn-outline">Batal</button>
              <button onClick={doEdit} className="imsyaf-btn-primary" data-testid="edit-guru-save">Simpan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
